import React, { useState, useMemo, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import PageWrapper from "../modules/PageWrapper";
import { CardComp, ModalCompAdvanceUpdate } from "../../../components";
import MemberRecordContributionForm from "./modules/MemberRecordContributionForm";
import MemberPaymentSelection from "./modules/MemberPaymentSelection";
import MemberPaymentModeChooser from "../modules/MemberPaymentModeChooser";
import {
  useGetMemberContributionCategoriesQuery,
  useCreateMemberOwnContributionMutation,
  useGetMemberPaymentMethodsQuery,
  useGetMemberPaymentFeePreviewQuery,
  useGetMemberCompanyBanksQuery,
  useCreateMemberContributionBankTransferMutation,
} from "../../../store/member/member-contribution";
import { formatMoney, getMemberTransactionChargesTotal } from "../../../utils";
import {
  feePreviewChannel,
  getFrontendBaseUrl,
  isPaystackMode,
  isSocietyBankMode,
  redirectToMemberOnlinePayment,
} from "../../../utils/memberOnlinePayment";
import FallbackImage from "../../../components/Common/ImageWrapper";
import MemberPendingBankTransfersCard from "../modules/MemberPendingBankTransfersCard";
import MemberPendingOnlinePaymentsCard from "../modules/MemberPendingOnlinePaymentsCard";

const RecordContribution = () => {
  const [paymentMode, setPaymentMode] = useState("paystack"); // paystack | alatpay | bank
  const [selectedBankId, setSelectedBankId] = useState("");
  const [bankNote, setBankNote] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [pageName, setPageName] = useState("");
  const [showWarning, setShowWarning] = useState(true);

  // Get contribution categories
  const { data: contributionCategoriesData, isLoading: isLoadingCategories } =
    useGetMemberContributionCategoriesQuery();

  // Get payment methods to find Paystack payment type ID
  const { data: paymentMethodsData } = useGetMemberPaymentMethodsQuery();
  const { data: companyBanks = [] } = useGetMemberCompanyBanksQuery();

  // Find Paystack payment type ID
  const paystackPaymentTypeId = useMemo(() => {
    if (!paymentMethodsData) return null;
    const paystackMethod = paymentMethodsData.find(
      (method) =>
        method?.name?.toUpperCase().includes("PAYSTACK") ||
        method?.name?.toUpperCase().includes("CARD")
    );
    return paystackMethod?.id || null;
  }, [paymentMethodsData]);

  const selectedBank = useMemo(
    () =>
      (companyBanks || []).find(
        (b) => String(b?.id) === String(selectedBankId)
      ),
    [companyBanks, selectedBankId]
  );

  // Format contribution categories for dropdown
  const formattedContributionCategories = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const categories = contributionCategoriesData?.categories || [];

    return categories
      .filter((item) => {
        // Filter by date range: start_date <= today <= end_date
        const startDate = item.start_date ? new Date(item.start_date) : null;
        const endDate = item.end_date ? new Date(item.end_date) : null;

        if (startDate) {
          startDate.setHours(0, 0, 0, 0);
          if (today < startDate) return false;
        }

        if (endDate) {
          endDate.setHours(0, 0, 0, 0);
          if (today > endDate) return false;
        }

        return true;
      })
      .map((item) => {
        const amountLabel = item.amount
          ? `Max: ${formatMoney(item.amount)}`
          : "No limit";
        return {
          label: `${item.name} | ${amountLabel}`,
          value: item.id,
          maxAmount: item.amount || null,
          item: item,
        };
      });
  }, [contributionCategoriesData]);

  // Formik setup - supports multiple contributions like staff
  // Payment date is automatically set to current date when submitting
  const initialValues = {
    formParts: [
      {
        contribution_category_id: "",
        amount: "",
      },
    ],
  };

  const validationSchema = Yup.object().shape({
    formParts: Yup.array().of(
      Yup.object().shape({
        contribution_category_id: Yup.string()
          .required("Contribution category is required")
          .test(
            "unique-contribution",
            "This contribution has already been selected in another entry",
            function (value) {
              if (!value || value === "") return true;
              const pathParts = this.path?.split("[") || [];
              const currentIndexStr = pathParts[1]?.split("]")[0];
              const currentIndex = currentIndexStr
                ? parseInt(currentIndexStr)
                : -1;
              if (currentIndex === -1) return true;
              const allFormParts =
                this.from?.[1]?.value?.formParts ||
                this.parent?.parent?.formParts ||
                [];
              const isDuplicate = allFormParts.some(
                (part, idx) =>
                  idx !== currentIndex &&
                  part?.contribution_category_id &&
                  part.contribution_category_id === value &&
                  part.contribution_category_id !== ""
              );
              return !isDuplicate;
            }
          ),
        amount: Yup.number()
          .required("Amount is required")
          .positive("Amount must be greater than 0")
          .test(
            "max-amount",
            "Amount exceeds the maximum allowed for this contribution",
            function (value) {
              if (!value) return true;
              const contributionId = this.parent.contribution_category_id;
              const contribution = formattedContributionCategories?.find(
                (cat) => cat.value === contributionId
              );
              if (!contribution?.maxAmount) return true;
              return parseFloat(value) <= parseFloat(contribution.maxAmount);
            }
          ),
      })
    ),
  });

  const handlePaymentModeChange = useCallback((mode) => {
    setPaymentMode(mode);
    if (isSocietyBankMode(mode)) {
      setSelectedPaymentMethod(null);
    } else {
      setSelectedBankId("");
      setBankNote("");
    }
  }, []);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => {
      if (isPaystackMode(paymentMode) && !selectedPaymentMethod) {
        toast.error("Please select a payment method");
        return;
      }
      if (isSocietyBankMode(paymentMode) && !selectedBankId) {
        toast.error("Please select the society account you paid into");
        return;
      }
      setPageName("Review");
    },
  });

  // Add form part
  const handleAddForm = () => {
    formik.setFieldValue("formParts", [
      ...formik.values.formParts,
      {
        contribution_category_id: "",
        amount: "",
      },
    ]);
  };

  // Remove form part
  const handleRemove = (indexToRemove) => {
    formik.setFieldValue(
      "formParts",
      formik.values.formParts.filter((_, index) => index !== indexToRemove)
    );
  };

  const [createContribution, { isLoading: isCreating }] =
    useCreateMemberOwnContributionMutation();
  const [createBankTransfer, { isLoading: isCreatingBank }] =
    useCreateMemberContributionBankTransferMutation();

  const handleFormSubmit = async (values) => {
    if (isSocietyBankMode(paymentMode)) {
      if (!selectedBankId) {
        toast.error("Please select the society account you paid into");
        return;
      }
      try {
        const currentDate = new Date().toISOString().split("T")[0];
        const payload = {
          company_bank_id: parseInt(selectedBankId, 10),
          member_note: bankNote?.trim() || undefined,
          fields: values.formParts.map((formPart) => ({
            contribution_id: parseInt(formPart.contribution_category_id, 10),
            amount: parseFloat(formPart.amount),
            payment_date: currentDate,
          })),
        };
        const result = await createBankTransfer(payload).unwrap();
        if (!result?.status) {
          toast.error(result?.message || "Request failed");
          return;
        }
        toast.success(
          "Request recorded. Transfer the exact amount shown; staff will confirm after verifying your deposit."
        );
        setPageName("");
        formik.resetForm();
        setSelectedBankId("");
        setBankNote("");
        return;
      } catch (error) {
        toast.error(
          error?.data?.message ||
            error?.message ||
            "An error occurred. Please try again."
        );
        return;
      }
    }

    if (isPaystackMode(paymentMode) && !selectedPaymentMethod) {
      toast.error("Please select a payment method");
      return;
    }

    if (isPaystackMode(paymentMode) && !paystackPaymentTypeId) {
      toast.error(
        "Payment method not available. Please refresh and try again."
      );
      return;
    }

    try {
      // Backend now accepts 'fields' array with multiple contributions
      // Format: { fields: [{ contribution_id, amount, payment_date }, ...] }
      // Payment date is automatically set to current date (when payment is made)
      const currentDate = new Date().toISOString().split("T")[0];
      const payload = {
        payment_provider: isPaystackMode(paymentMode) ? "paystack" : "alatpay",
        frontend_base_url: getFrontendBaseUrl(),
        fields: values.formParts.map((formPart) => ({
          contribution_id: parseInt(formPart.contribution_category_id),
          amount: parseFloat(formPart.amount),
          payment_date: currentDate, // Always use current date - payment is made now
        })),
      };

      const response = await createContribution(payload);

      // Check if request succeeded
      if (!response.data?.status) {
        const errorMessage =
          response.data?.message || "Failed to create contribution";
        toast.error(errorMessage);
        return;
      }

      toast.success("Redirecting to payment page...");
      if (!redirectToMemberOnlinePayment(response.data)) {
        toast.error("Payment URL not received. Please try again.");
      }
    } catch (error) {
      console.error("Contribution creation error:", error);
      toast.error(
        error?.data?.message ||
          error?.message ||
          "An error occurred. Please try again."
      );
    }
  };

  // Calculate total amount
  const totalAmount = formik.values.formParts.reduce(
    (sum, part) => sum + parseFloat(part.amount || 0),
    0
  );

  const { data: feePreview } = useGetMemberPaymentFeePreviewQuery(
    {
      principal_amount: totalAmount,
      payment_channel: feePreviewChannel(paymentMode),
    },
    { skip: !totalAmount || totalAmount <= 0 }
  );

  // Prepare contributions for review modal
  const contributions = formik.values.formParts.map((part) => {
    const contributionCategory = formattedContributionCategories?.find(
      (category) => category.value === part.contribution_category_id
    );
    const contributionItem = contributionCategory?.item;
    const contributionName = contributionCategory
      ? contributionCategory.item?.name ||
        contributionCategory.label.split(" | ")[0]
      : "";
    const isWithdrawable = contributionItem?.is_withdrawable === 1;
    const maxAmount = contributionItem?.amount;

    return {
      name: contributionName,
      amount: part.amount || "",
      isWithdrawable: isWithdrawable,
      maxAmount: maxAmount,
    };
  });

  const useModal = [
    {
      name: "Review",
      component: (
        <div className="w-full py-5 px-3 px-lg-4 min-h-[200px]">
          <div className="w-full justify-center items-center flex gap-2 pb-4 position-relative">
            <img
              src="/Icon/cancel.svg"
              role="button"
              onClick={() => setPageName("")}
              alt="close"
              className="close-img h-5 position-absolute"
              style={{ right: "15px", top: "15px", cursor: "pointer" }}
            />
            <p className="text-[#1E1E1E] font-bold text-2xl">
              <span className="text-[var(--color-primary)]">Contribution</span> Review
            </p>
          </div>

          {/* Payment path */}
          {isPaystackMode(paymentMode) && selectedPaymentMethod && (
            <div
              className="mb-3 p-3 rounded"
              style={{ backgroundColor: "#f7f7f7" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-sm text-[#525252]">Payment method:</span>
                <span className="text-sm fw-semibold text-[#1E1E1E]">
                  {selectedPaymentMethod.label || "Pay with New Card"}
                </span>
              </div>
            </div>
          )}
          {paymentMode === "alatpay" && (
            <div
              className="mb-3 p-3 rounded"
              style={{ backgroundColor: "#f7f7f7" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-sm text-[#525252]">Payment method:</span>
                <span className="text-sm fw-semibold text-[#1E1E1E]">
                  ALATPay
                </span>
              </div>
            </div>
          )}
          {isSocietyBankMode(paymentMode) && selectedBank && (
            <div
              className="mb-3 p-3 rounded"
              style={{ backgroundColor: "#f7f7f7" }}
            >
              <p className="text-sm fw-semibold text-[#1E1E1E] mb-2">
                Transfer to this society account (exact amount below)
              </p>
              <p className="text-sm mb-1">{selectedBank.bank_name}</p>
              <p className="text-sm mb-1">{selectedBank.account_name}</p>
              <p className="text-sm fw-semibold mb-0">
                {selectedBank.account_number}
              </p>
            </div>
          )}

          {/* Contributions List */}
          <div className="flex flex-col gap-3 border-top border-bottom py-3 mb-3">
            {contributions.map((item, i) => (
              <div
                key={i}
                className="d-flex justify-content-between align-items-start"
              >
                <div className="flex-1">
                  <p className="text-sm fw-semibold text-[#1E1E1E] mb-1">
                    {item.name}
                  </p>
                  <div className="d-flex gap-2 align-items-center flex-wrap">
                    <span
                      className="px-2 py-1 rounded-pill text-white"
                      style={{
                        fontSize: "10px",
                        backgroundColor: item.isWithdrawable
                          ? "var(--color-secondary)"
                          : "var(--color-primary)",
                      }}
                    >
                      {item.isWithdrawable
                        ? "Withdrawable"
                        : "Non-Withdrawable"}
                    </span>
                    {item.maxAmount && (
                      <span className="text-xs text-muted">
                        Max: {formatMoney(item.maxAmount)}
                      </span>
                    )}
                    {!item.maxAmount && (
                      <span className="text-xs text-muted">No limit</span>
                    )}
                  </div>
                </div>
                <p className="font-bold text-sm text-[#1E1E1E] ms-3">
                  {formatMoney(item.amount)}
                </p>
              </div>
            ))}
          </div>

          {/* Total */}
          <div className="w-full mt-3 pt-3 border-top d-flex flex-column gap-2">
            <div className="d-flex justify-content-between align-items-center">
              <p className="text-sm text-[#525252] mb-0">Principal</p>
              <p className="text-sm fw-semibold mb-0">{formatMoney(totalAmount, "NGN")}</p>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <p className="text-sm text-[#525252] mb-0">Transaction charges</p>
              <p className="text-sm fw-semibold mb-0">
                {isSocietyBankMode(paymentMode)
                  ? formatMoney(0, "NGN")
                  : formatMoney(
                      getMemberTransactionChargesTotal(feePreview),
                      "NGN"
                    )}
              </p>
            </div>
            <div className="d-flex justify-content-between align-items-center border-top pt-2 mt-1">
              <p className="text-base fw-semibold text-[#1E1E1E] mb-0">
                {isSocietyBankMode(paymentMode)
                  ? "Exact amount to transfer"
                  : "Total to Pay"}
              </p>
              <p className="font-bold text-lg text-[var(--color-primary)] mb-0">
                {formatMoney(
                  isSocietyBankMode(paymentMode)
                    ? totalAmount
                    : feePreview?.member_payable_total || totalAmount,
                  "NGN"
                )}
              </p>
            </div>
            {!isSocietyBankMode(paymentMode) &&
              (feePreview?.convenience_fee_org_portion > 0 ||
                feePreview?.organization_gateway_fee_estimate > 0) && (
                <p className="text-xs text-muted mb-0">
                  Some charges are covered by your cooperative.
                </p>
              )}
            {isSocietyBankMode(paymentMode) && (
              <p className="text-xs text-muted mb-0">
                No card or platform fees on bank transfer. Staff will confirm
                after they see your deposit.
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div className="w-full flex justify-center items-center mt-4">
            <button
              onClick={() => handleFormSubmit(formik.values)}
              className="h-12 w-full max-w-[250px] rounded-[10px] bg-[var(--color-primary)] text-white fw-semibold"
              disabled={isCreating || isCreatingBank}
              style={{ transition: "all 0.3s ease" }}
            >
              {isCreating || isCreatingBank
                ? "Submitting..."
                : isSocietyBankMode(paymentMode)
                ? "Submit transfer request"
                : "Submit Contribution"}
            </button>
          </div>
        </div>
      ),
    },
  ];

  return (
    <PageWrapper title="Record Contribution" back>
      <div className="px-3 px-lg-4">
        <main className="w-full flex flex-col gap-5 pb-5">
          <CardComp cardClassName="border-0">
            <div className="px-4 lg:px-6 py-4">
              <div className="d-flex justify-content-between align-items-start gap-3">
                <div>
                  <h2 className="text-black1 text-2xl fw-bold mb-1">
                    Record Contribution
                  </h2>
                  {showWarning && (
                    <p className="text-muted small mb-0">
                      Tip: You can add more than one contribution at a time.
                    </p>
                  )}
                </div>
                {showWarning && (
                  <button
                    type="button"
                    onClick={() => setShowWarning(false)}
                    className="border-0 bg-transparent p-0"
                    style={{ cursor: "pointer", marginTop: "2px" }}
                    aria-label="Hide tip"
                    title="Hide tip"
                  >
                    <FallbackImage
                      src="/Icon/cancel.svg"
                      width={18}
                      height={18}
                    />
                  </button>
                )}
              </div>
            </div>
          </CardComp>

          <MemberPendingOnlinePaymentsCard flowFilter="contribution" />
          <MemberPendingBankTransfersCard flowFilter="contribution" />

          <section className="w-full">
            <form className="w-full" onSubmit={formik.handleSubmit}>
              <CardComp
                cardClassName="border-0"
                style={{ backgroundColor: "#ffffff" }}
              >
                <div className="w-full flex flex-col gap-6 p-5 lg:p-10">
                  {formik.values.formParts.map((form, index) => (
                    <MemberRecordContributionForm
                      key={index}
                      index={index}
                      formik={formik}
                      handleRemove={handleRemove}
                      formattedContributionCategories={
                        formattedContributionCategories
                      }
                    />
                  ))}

                  <button
                    type="button"
                    className="bg-[var(--color-primary)] w-[200px] disabled:opacity-50 rounded-lg h-12 text-white fw-semibold hover:opacity-90 transition-opacity"
                    onClick={handleAddForm}
                    disabled={!(formik.isValid && formik.dirty)}
                  >
                    Add New
                  </button>
                </div>
              </CardComp>

              {/* Payment Method Selection - Show when at least one contribution is filled */}
              {totalAmount > 0 && (
                <div className="mt-5">
                  <CardComp
                    cardClassName="border-0 pt-3 px-3"
                    style={{ backgroundColor: "#fafafa" }}
                  >
                    <MemberPaymentModeChooser
                      paymentMode={paymentMode}
                      onPaymentModeChange={handlePaymentModeChange}
                      companyBanks={companyBanks}
                      selectedBankId={selectedBankId}
                      onSelectedBankIdChange={setSelectedBankId}
                      memberNote={bankNote}
                      onMemberNoteChange={setBankNote}
                    >
                      <MemberPaymentSelection
                        selectedPaymentMethod={selectedPaymentMethod}
                        onSelectPaymentMethod={setSelectedPaymentMethod}
                        amount={totalAmount}
                      />
                      {!selectedPaymentMethod && (
                        <p className="text-danger small mt-2">
                          Please select a payment method to continue
                        </p>
                      )}
                    </MemberPaymentModeChooser>
                  </CardComp>
                </div>
              )}

              <div className="mt-5 d-flex justify-content-start">
                <button
                  type="submit"
                  className="bg-[var(--color-primary)] w-[200px] rounded-lg h-12 text-white fw-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={
                    !formik.isValid ||
                    totalAmount === 0 ||
                    (isPaystackMode(paymentMode) && !selectedPaymentMethod) ||
                    (isSocietyBankMode(paymentMode) && !selectedBankId)
                  }
                >
                  Review & Submit
                </button>
              </div>
            </form>
          </section>

          <ModalCompAdvanceUpdate
            size={"md"}
            pageName={pageName}
            centered
            arrayComp={useModal}
            handleClose={() => setPageName("")}
          />
        </main>
      </div>
    </PageWrapper>
  );
};

export default RecordContribution;
